import { useState, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WelletContext } from '../../../context/WelletContext';
import { format, parseISO } from 'date-fns';
import { getLocale } from '../../../helpers/dates';
import { useActivities } from '../../../hooks/useActivities';
import { SkeletonPoints } from '../../../components/Skeletons/SkeletonPoints';
import { getAssociateStatusAccount } from '../../../actions/associatesActions';
import { useTranslation } from 'react-i18next';
import ContentApp from '../../../components/ContentApp';
import StatusTracker from '../../../components/Points/StatusTracker/StatusTracker';
import StatusCommissions from '../../../components/Points/StatusCommissions/StatusCommissions';
import Activity from '../../../components/Activities/Activity/Activity';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import CustomerNav from '../../../components/CustomerNav/CustomerNav';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import styles from './pointsScreen.module.css';
import ActivitiesHome from '../../../components/Activities/ActivitiesHome/ActivitiesHome';
import HeaderUserPoints from '../../../components/Points/HeaderUserPoints';
import CommissionScreen from '../CommissionScreen/CommissionScreen';
import { Spinner } from '../../../components/Common/Spinner/Spinner';
import { CircleSpinner } from 'react-spinners-kit';

const PointsScreen = ({ history }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.associates.user);
  const affiliatePoints = user.salesCategory.points;
  const categoryName = user.salesCategory.category;
  const jwt = useSelector((state) => state.associates.jwt);
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const [categories, setCategories] = useState(null);
  const [affiliateType, setAffiliateType] = useState();
  const [loading, setLoading] = useState(true);
  const app = useSelector((state) => state.app);
  const locale = getLocale(app.language);
  const schemeType = useSelector((state) => state?.associates?.user?.salesCategory.schemeType);
  const [expirationDate, setExpirationDate] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const handleVerifyAccount = () => {
    history.push('/account-verification');
  };

  const getCategories = async () => {
    setLoading(true);
    try {
      const response = await welletContext.apis.tickets.get(`associates/getAllsalesCategory`);
      setAffiliateType(response?.data.affiliateType);
      if (response.status === 200) {
        const sortedCategories = response.data.categoriesList.sort((a, b) => b.level - a.level);
        setCategories(sortedCategories);
        const expirationDate = response.data.nextReCategorization;

        setExpirationDate(expirationDate);

        const lastUpdate = response.data.lastUpdate;
        setLastUpdate(lastUpdate);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getAssociateStatusAccount(welletContext, jwt));
  }, [jwt]);

  const pointsInfoData = useActivities({
    pageSize: 5,
    endpoint: '/associates/getpoints',
    api: welletContext.apis.tickets,
  });

  useEffect(() => {
    if (!categories) {
      getCategories();
    }
  }, []);

  return (
    <>
      <AccountHeader title={app.titleWeb} icon='config' pathName='/configuration' />
      <ContentApp header menu>
        {loading || !affiliateType ? (
          <div className='loaderDiv'>
            <CircleSpinner
              className='spinner-app mx-auto'
              style={{ margin: 'auto' }}
              size={40}
              color='var(--color-font-primary)'
            />
          </div>
        ) : schemeType === 'FIXED' ? (
          <CommissionScreen
            affiliateName={user.name}
            commissionPercentage={categories?.[0]?.commissionPercentage}
            messageLoyalty={affiliateType === 'CONCIERGE' && schemeType === 'FIXED'}
            handleVerifyAccount={handleVerifyAccount}
          />
        ) : (
          <div className='points'>
            <HeaderUserPoints
              user={user}
              expirationDate={expirationDate}
              category={categoryName}
              points={affiliatePoints}
            />
            <div className='card mt-2'>
              <StatusTracker
                title={t('leaderboard')}
                points={Number(affiliatePoints)}
                categories={categories}
                loading={loading}
                categoryName={categoryName}
              />
            </div>
            <div className='mt-2'>
              <ActivitiesHome
                title={<div className='bold'>{t('recentAccountActivities')}</div>}
                linkTitle={
                  pointsInfoData?.activities?.length === 5 ? t('allAccountActivity') : null
                }
                pathName={'/points-activities'}
                history={history}
              >
                {pointsInfoData?.activities?.length === 0 ? (
                  <EmptyActivity
                    title={t('noPoints')}
                    subtitle={t('noPointsDescription')}
                    icon={'notification'}
                  />
                ) : (
                  pointsInfoData?.activities?.map((p, index) => (
                    <Activity
                      key={index}
                      loading={pointsInfoData.loading}
                      hasMore={pointsInfoData.hasMore}
                      loadMore={pointsInfoData.loadMore}
                      title={`${t('pointsReservation')} ${p.referenceCode}`}
                      subtitle={<div className='text-secondary'>{p.showName}</div>}
                      item2={
                        <div className='bold'>{`${
                          p.points > 0 ? `+ ${p.points}` : `- ${Math.abs(p.points)}`
                        }`}</div>
                      }
                      item3={
                        <div className='text-secondary text-right'>
                          {format(parseISO(p.operationDate), 'dd MMM', { locale })}
                        </div>
                      }
                      className={`${
                        index === pointsInfoData.activities.length - 1 ? '' : 'feed-item-border'
                      } ${styles.activity}`}
                    />
                  ))
                )}
              </ActivitiesHome>
            </div>

            <div className='mt-2'>
              <StatusCommissions
                categoryName={categoryName}
                categories={categories}
                lastUpdate={lastUpdate}
                title={
                  <>
                    {app.appType === 'AFFILIATE' && (
                      <> {t('commissionStatusTitle', { organization: app.name })} </>
                    )}
                    {app.appType === 'CONSUMER' && (
                      <> {t('discountsStatusTitle', { organization: app.name })} </>
                    )}
                  </>
                }
              />
            </div>
          </div>
        )}
        <CustomerNav history={history} />
      </ContentApp>
    </>
  );
};

export default PointsScreen;
